/**
 * Toast queue.
 *
 * Deliberately framework-free. Toasts are raised from places that are not React
 * and never will be — the axios error interceptor, the redux middlewares, socket
 * ack callbacks — so the queue cannot live in component state and cannot depend
 * on a hook being in scope. `Toaster` is the only subscriber; everything else
 * calls `push` (through `Utils.showMessage`) and forgets about it.
 *
 * This replaces antd `message`, whose notice markup left no room for a dismiss
 * control, a timer rail or per-kind tinting, and whose stack sits top-centre
 * over the conversation header.
 */

/** Kinds the UI knows how to paint. Anything else falls back to `info`. */
export const TOAST_KINDS = ['success', 'error', 'warning', 'info', 'loading'];

/**
 * Visible seconds per kind. A success can be brief — the screen behind it
 * already changed. A warning or an error is usually reporting that something the
 * user asked for did not happen, which needs long enough to actually read, and
 * `loading` sticks until the caller dismisses it.
 *
 * Hovering the stack pauses these, so the longer values are not a trap.
 */
const DURATIONS = { success: 3, info: 4, warning: 5, error: 6, loading: 0 };

/**
 * Ceiling on the visible stack; the oldest is dropped past it.
 *
 * The cap matters more than it looks: a message fired from inside a loop (one
 * per rejected file in a selection, say) would otherwise bury the screen.
 * Identical messages collapse into a repeat count before this ever applies.
 */
export const MAX_VISIBLE = 3;

let toasts = [];
let seq = 0;
const listeners = new Set();

const emit = () => {
  for (const listener of listeners) listener(toasts);
};

/** Current queue. Treat as immutable — every mutation replaces the array. */
export const getToasts = () => toasts;

export const subscribe = (listener) => {
  listeners.add(listener);
  listener(toasts);
  return () => listeners.delete(listener);
};

export const dismiss = (id) => {
  const next = toasts.filter((toast) => toast.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
};

export const clear = () => {
  if (!toasts.length) return;
  toasts = [];
  emit();
};

/**
 * Raise (or refresh) a toast.
 *
 * @param status  one of TOAST_KINDS
 * @param content message text
 * @param options `{ key, duration, title }`. `duration` is in seconds; 0 pins
 *                the toast until it is dismissed. A stable `key` refreshes the
 *                existing toast in place instead of stacking another one.
 * @returns a function that dismisses this toast.
 */
export const push = (status, content, options = {}) => {
  const text = typeof content === 'string' ? content.trim() : content;
  // antd rendered an empty pill for a blank message, which is how a failed
  // request with no `message` field produced a floating white rectangle.
  if (!text) return () => {};

  const kind = TOAST_KINDS.includes(status) ? status : 'info';
  const id = options.key ?? `toast-${(seq += 1)}`;
  const duration = Math.max(0, options.duration ?? DURATIONS[kind] ?? 4);
  const title = options.title ?? null;

  // Refresh in place on an explicit key OR on an identical message: the same
  // rejection arriving three times is one fact, not three toasts.
  const keyed = toasts.findIndex((toast) => toast.id === id);
  const repeated = toasts.findIndex((toast) => toast.kind === kind && toast.content === text);
  const at = keyed >= 0 ? keyed : repeated;

  if (at >= 0) {
    const previous = toasts[at];
    const next = {
      ...previous,
      kind,
      content: text,
      title,
      duration,
      // Bumping the revision restarts the timer and the life bar.
      revision: previous.revision + 1,
      repeat: previous.content === text ? previous.repeat + 1 : 1,
    };
    toasts = toasts.map((toast, index) => (index === at ? next : toast));
  } else {
    toasts = [
      ...toasts,
      { id, kind, content: text, title, duration, revision: 0, repeat: 1 },
    ].slice(-MAX_VISIBLE);
  }

  emit();
  return () => dismiss(id);
};
